import { useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import _compact from 'lodash/compact';

import { selectWysiwigEditorEnabled } from 'core/userContext/selectors';
import {
  EmailContentDefinition,
  HtmlEditor,
  InAppContentDefinition,
  MessageMedium,
} from 'modules/graphql/types';
import { spacing } from 'ui/theme';
import noop from 'utils/noop';
import { displayContent } from 'modules/templateEditor/common';
import { CONTENT_EDITOR_OPTIONS } from 'modules/templateEditor/common/constants';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { useContentEditorSelectorKeyPress } from './hooks/useContentEditorSelectorKeyPress';
import { ContentEditorOption } from './ContentEditorOption';

export const CONTENT_EDITOR_OPTION_CONTAINER_TEST_ID = 'content-editor-option-container';
export const WYSIWYG_CONTENT_EDITOR_OPTION_TEST_ID = 'wysiwyg-option';
export const DRAG_AND_DROP_CONTENT_EDITOR_OPTION_TEST_ID = 'drag-and-drop-option';
export const SIDE_BY_SIDE_CONTENT_EDITOR_OPTION_TEST_ID = 'side-by-side-option';

const OptionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${spacing.double};
  gap: ${spacing.double};
`;

type ContentEditorSelectorProps = {
  selectedEditor: HtmlEditor;
  setSelectedEditor: (arg: HtmlEditor) => void;
};

export const ContentEditorSelector = ({
  setSelectedEditor = noop,
  selectedEditor,
}: ContentEditorSelectorProps) => {
  const { liveTemplate } = useEditableFieldsContext();
  const { medium } = useTemplateQueryContext();

  const handleKeyPress = useContentEditorSelectorKeyPress();

  const mediumData = (liveTemplate?.mediumData || {}) as
    | EmailContentDefinition
    | InAppContentDefinition;
  const hideDragAndDrop = !!mediumData.html && !mediumData.beeBuilderMetadata; // otherwise the html will be discarded
  const wysiwygEditorEnabled =
    useSelector(selectWysiwigEditorEnabled) || medium === MessageMedium.InApp;
  const refDragDrop = useRef<HTMLDivElement>(null);
  const refWysiwyg = useRef<HTMLDivElement>(null);
  const refSideBySide = useRef<HTMLDivElement>(null);
  const refs = _compact([
    !hideDragAndDrop && refDragDrop,
    wysiwygEditorEnabled && refWysiwyg,
    refSideBySide,
  ]);

  return (
    <OptionContainer
      onKeyDown={(e) => handleKeyPress(e, refs)}
      data-test={CONTENT_EDITOR_OPTION_CONTAINER_TEST_ID}
    >
      <ContentEditorOption
        data-test={DRAG_AND_DROP_CONTENT_EDITOR_OPTION_TEST_ID}
        content={{
          title: displayContent.labelDragDrop,
          tooltip: displayContent.tooltipDragDrop,
          ...CONTENT_EDITOR_OPTIONS.DRAG_AND_DROP,
        }}
        contentEditor={HtmlEditor.Bee}
        selectedEditor={selectedEditor}
        setSelectedEditor={setSelectedEditor}
        ref={refDragDrop}
        autoFocus
      />
      {wysiwygEditorEnabled && (
        <ContentEditorOption
          data-test={WYSIWYG_CONTENT_EDITOR_OPTION_TEST_ID}
          content={{
            title: displayContent.labelWysiwyg,
            tooltip: displayContent.tooltipWysiwyg,
            ...CONTENT_EDITOR_OPTIONS.WYSIWYG,
          }}
          contentEditor={HtmlEditor.Wysiwyg}
          selectedEditor={selectedEditor}
          setSelectedEditor={setSelectedEditor}
          ref={refWysiwyg}
        />
      )}
      <ContentEditorOption
        data-test={SIDE_BY_SIDE_CONTENT_EDITOR_OPTION_TEST_ID}
        content={{
          title: displayContent.labelSideBySide,
          tooltip: displayContent.tooltipSideBySide,
          ...CONTENT_EDITOR_OPTIONS.SIDE_BY_SIDE,
        }}
        contentEditor={HtmlEditor.Ace}
        selectedEditor={selectedEditor}
        setSelectedEditor={setSelectedEditor}
        ref={refSideBySide}
      />
    </OptionContainer>
  );
};
